import { useContext, useEffect, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { capitalizeWord } from 'utils/stringUtils';
import Device from './GlobalActions/If/Device';
import DateTime from './GlobalActions/If/DateTime';
import DeviceAction from './GlobalActions/then/DeviceAction';
import SendEmail from './GlobalActions/then/SendEmail';
import { PiWarningFill } from 'react-icons/pi';
import { ColorContext } from 'App';
import DeviceGroup from './GlobalActions/If/DeviceGroup';

// dont allow select -select-
// insert node needs default error state

const GlobalActionsDropdown = ({
  itemNode,
  menuItems,
  setRetrigger,
  submitHandler,
  anyErrors,
  submitting,
}) => {
  const theme = useContext(ColorContext);
  const nextActions = {
    if: ['-Select-', 'Or', 'And', 'Then'],
    then: ['-Select-', 'And', 'Until'],
    until: ['-Select-', 'And', 'Done'],
    done: ['-Select-', 'Submit'],
  };

  const mapActionsToType = {
    Or: 'if',
    And: 'if',
    Then: 'then',
    Andthen: 'then',
    Untilthen: 'until',
    Donethen: 'done',
    Anduntil: 'until',
    Until: 'until',
    Doneuntil: 'done',
    Done: 'done',
  };

  const mapActionsToBaseAction = {
    Or: 'Or',
    And: 'And',
    Then: 'Then',
    Andthen: 'And',
    Untilthen: 'Until',
    Donethen: 'Done',
    Anduntil: 'And',
    Until: 'Until',
    Doneuntil: 'Done',
    Done: 'Done',
  };

  const isLastOfType = () => {
    return itemNode.next ? itemNode.next.type !== itemNode.type : true;
  };

  const changeIntegration = (value) => {
    if (value === '-Select-') {
      return;
    }
    itemNode.integrationType = value;

    setRetrigger(Date.now());
  };
  const setDropdown = (value) => {
    itemNode.showDropdown = value;
    setRetrigger(Date.now());
  };

  const toggleDropdown = () => setDropdown(!itemNode.showDropdown);

  const changeIntegrationData = (value) => {
    itemNode.integrationData = value;

    setRetrigger(new Date());
  };

  const [error, setError] = useState(null);

  const integrationTypes = {
    //IF/UNTIL
    Device: (
      <Device setData={changeIntegrationData} data={itemNode.integrationData} setError={setError} />
    ),
    'Device Group': (
      <DeviceGroup
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    'Date & Time': (
      <DateTime
        setData={changeIntegrationData}
        type={'dateTime'}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    Date: (
      <DateTime
        type={'date'}
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    Time: (
      <DateTime
        type={'time'}
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    'Original Condition Resolved': '',
    'Start Manually': null,
    'Stop Manually': null,
    Places: null,
    Alerts: null,

    'Device Action': (
      <DeviceAction
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    'Device Group Action': (
      <DeviceAction
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
        isGroup={true}
      />
    ),
    'Send Email': (
      <SendEmail
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
        sms={false}
      />
    ),
    'Send SMS': (
      <SendEmail
        sms={true}
        setData={changeIntegrationData}
        data={itemNode.integrationData}
        setError={setError}
      />
    ),
    'Create Custom Alert': null,
    'Log Event(s)': null,
    'Send Commands / Adjust Settings': null,
    'Data Analysis and Reporting': null,
    'Time Limit': null,
  };

  const onNextActionChange = (value) => {
    itemNode.selectedAction = value;

    if (value === '-Select-') {
      setRetrigger(Date.now());
      return;
    }

    //if there is no next node, must be to add a new action
    if (!itemNode.next) {
      addNode(value);
    }

    //if there is a next node
    if (itemNode.next) {
      //if next type is different, insert a new item
      if (itemNode.type !== itemNode.next.type) {
        //if the next action is "Or" or "And", insert a new item
        if (value === 'Or' || value === 'And' || value == 'Andthen' || value == 'Anduntil') {
          insertNode(value);
        }
      }

      if (itemNode.type === 'then' && itemNode.next.type === 'done' && value === 'Untilthen') {
        let temp = itemNode.next;
        itemNode.next = {
          type: mapActionsToType[value],
          selectedAction: 'Doneuntil',
          integrationData: {
            error: true,
          },
          next: temp,
          prev: itemNode,
        };
        temp.prev = itemNode.next;
      }

      if (itemNode.type === 'then' && itemNode.next.type === 'until' && value === 'Donethen') {
        itemNode.next = {
          type: mapActionsToType[value],
          selectedAction: 'Doneuntil',
          integrationData: {
            error: true,
          },
          next: null,
          prev: itemNode,
        };
      }
    }
    itemNode.selectedAction = value;
    setRetrigger(Date.now());
  };

  useEffect(() => {
    if (!itemNode.next && !itemNode.prev) return;

    if (isLastOfType() && itemNode.type === 'if' && itemNode.selectedAction !== '-Select-') {
      itemNode.selectedAction = nextActions[itemNode.type][nextActions[itemNode.type].length - 1];
    }
    if (isLastOfType() && itemNode.type === 'then' && itemNode.selectedAction !== '-Select-') {
      itemNode.selectedAction = nextActions[itemNode.type][nextActions[itemNode.type].length - 1];
    }
  }, [itemNode]);

  const insertNode = (value) => {
    let temp = itemNode.next;
    let newAction = itemNode.selectedAction;

    if (itemNode.type === 'if') {
      newAction = 'Then';
    }
    if (itemNode.type === 'then') {
      newAction = 'Until';
    }
    if (itemNode.type === 'until') {
      newAction = 'Done';
    }

    itemNode.next = {
      type: mapActionsToType[value],
      selectedAction: newAction,
      integrationType: '-Select-',
      integrationData: {
        error: true,
      },
      component: null,
      next: temp,
      prev: itemNode,
    };
    temp.prev = itemNode.next;
    setRetrigger(Date.now());
  };

  const addNode = (value) => {
    itemNode.next = {
      type: mapActionsToType[value],
      selectedAction: '-Select-',
      integrationType: '-Select-',
      integrationData: {
        error: true,
      },
      next: null,
      prev: itemNode,
    };
    // setError({message: "Item not selected"})
    setRetrigger(Date.now());
  };

  const removeNode = () => {
    if (itemNode.type === 'then') {
      // itemNode.next = null;
      itemNode.prev.selectedAction = '-Select-';
      if (itemNode.prev.type === 'if') {
        itemNode.next = null;
      }
      if (itemNode.prev.type === 'then' && itemNode.next && itemNode.next.type === 'until') {
        itemNode.prev.selectedAction = 'Until';
      }
    }
    if (itemNode.type === 'if') {
      itemNode.prev.selectedAction = 'Then';
    }
    if (itemNode.type === 'until') {
      itemNode.prev.selectedAction = '-Select-';
      itemNode.prev.next = null;
      itemNode.next = null;
    }

    if (itemNode.prev) {
      itemNode.prev.next = itemNode.next;
    }
    if (itemNode.next) {
      itemNode.next.prev = itemNode.prev;
    }

    setRetrigger(Date.now());
  };
  {
    /* Done branch */
  }
  if (itemNode.type === 'done')
    return (
      <div className="d-flex flex-column w-100">
        <div className="d-flex flex-row justify-content-between align-items-center flex-wrap gap-2">
          <div
            className="p-3 fs-4 text-truncate w-auto"
            style={{
              minWidth: 100,
              fontWeight: 'bold',
            }}
          >
            {capitalizeWord(
              (itemNode.prev &&
                itemNode.prev.type === itemNode.type &&
                mapActionsToBaseAction[itemNode.prev.selectedAction]) ||
                itemNode.type,
            )}
          </div>

          <div
            className="text-center ms-auto mt-2 mt-md-0 max-w-100 p-3 rounded-3"
            style={{
              cursor: 'pointer',
              background: anyErrors || submitting ? theme.primary : theme.primaryHighlight,
              width: 200,
            }}
            role="button"
            tabIndex={0}
            onClick={() => {
              submitHandler();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') submitHandler();
            }}
          >
            Submit
          </div>
        </div>
      </div>
    );

  {
    /* Main Return */
  }
  return (
    <div className="d-flex flex-column w-100 gap-3 mt-3 max-w-5xl">
      <div className="d-flex flex-column flex-md-row align-items-stretch w-100 gap-2">
        <div
          className={`d-flex flex-row align-items-center justify-content-between px-3 py-2 fs-4 
            ${
              itemNode.prev && mapActionsToBaseAction[itemNode.prev.selectedAction] === 'And'
                ? 'ms-md-5'
                : ''
            }`}
          style={{
            minWidth: 100,
            fontWeight: 'bold',
          }}
        >
          {capitalizeWord(
            (itemNode.prev &&
              itemNode.prev.type === itemNode.type &&
              mapActionsToBaseAction[itemNode.prev.selectedAction]) ||
              itemNode.type,
          )}
          {itemNode.integrationData &&
            itemNode.integrationData.error &&
            itemNode.integrationType !== 'Original Condition Resolved' && (
              <span className="text-danger ms-2">
                <PiWarningFill />
              </span>
            )}
        </div>
        {/* Control block */}
        <div
          className="d-flex flex-column flex-grow-1"
          style={{
            backgroundColor: theme.primaryShadow,
            borderRadius: 5,
          }}
        >
          <div
            className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3 p-3"
            style={{
              cursor: 'pointer',
              backgroundColor: theme.primaryShadow,
              borderRadius: 5,
            }}
            onClick={() => setDropdown(true)}
          >
            {/* Dropdown container */}
            <div
              className="d-flex align-items-center flex-nowrap flex-grow-1 w-100"
              onClick={(e) => e.stopPropagation()}
            >
              <select
                className="form-select p-3 w-100 mx-3"
                style={{
                  backgroundColor: theme.primaryShadow,
                  borderRadius: 5,
                  border: '1px solid lightgrey',
                }}
                value={itemNode.integrationType}
                onChange={(e) => {
                  if (integrationTypes[e.target.value] === null) return;
                  changeIntegration(e.target.value);
                  // Clear any inline error message when the integration type changes
                  setError({ message: '' });
                  const props = { setData: 'changeIntegrationData' };
                  itemNode.showDropdown = true;
                }}
              >
                {menuItems.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              {/* Right-side icons */}
              <div className="d-flex align-items-center ms-auto gap-4 flex-shrink-0">
                {isLastOfType() && itemNode.prev && (
                  <div
                    className="text-nowrap"
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={removeNode}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') removeNode();
                    }}
                    aria-label="Remove item"
                    title="Remove"
                  >
                    <FaTrash />
                  </div>
                )}
                {/* {itemNode.integrationType !== "-Select-" && (
                  <div className="text-nowrap" style={{ cursor: "pointer" }}>
                    {itemNode.showDropdown ? <BsChevronUp /> : <BsChevronDown />}
                  </div>
                )} */}
              </div>
            </div>
          </div>

          {/* Inline error under label */}
          {error && error.message && (
            <div className="px-3 pb-2">
              <div className="mt-2 fw-normal fs-6" style={{ color: 'red' }}>
                {error.message}
              </div>
            </div>
          )}

          {/* Dropdown content */}
          <div className="w-100">
            <div className={`${itemNode.showDropdown ? 'd-flex' : 'd-none'} w-100`}>
              {integrationTypes[itemNode.integrationType]}
            </div>
          </div>
        </div>
      </div>
      <div className="w-100">
        <div className="d-flex flex-wrap w-100">
          <div
            className="d-none d-md-block fs-4 p-2"
            style={{ width: '100px', fontWeight: 'bold' }}
          />
          {/* Buttons */}
          {nextActions[itemNode.type] && (
            <div className="d-flex flex-wrap gap-2 w-100">
              {nextActions[itemNode.type].map((item, index) => {
                if (item === '-Select-') return null;
                if (
                  index >= nextActions[itemNode.type].length - 2 &&
                  !isLastOfType() &&
                  itemNode.type === 'then'
                )
                  return null;
                if (index === nextActions[itemNode.type].length - 1 && !isLastOfType()) return null;

                const sharedStyle = {
                  cursor: 'pointer',
                  background:
                    itemNode.selectedAction === item ||
                    itemNode.selectedAction === item + itemNode.type
                      ? theme.primaryHighlight
                      : theme.primaryShadow,
                  padding: '12px 16px',
                  width: '20%',
                  minWidth: 90,
                  textAlign: 'center',
                  borderRadius: 5,
                };

                if (itemNode.type !== 'then' && itemNode.type !== 'until')
                  return (
                    <div
                      key={`${item}-${index}`}
                      className="text-truncate"
                      style={sharedStyle}
                      onClick={() => onNextActionChange(item)}
                    >
                      {item}
                    </div>
                  );

                return (
                  <div
                    key={`${item}-${index}`}
                    className="text-truncate"
                    style={sharedStyle}
                    onClick={() => onNextActionChange(item + itemNode.type)}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isLastOfType() && (
        <div className="w-100" style={{ height: 40, borderBottom: '1px solid grey' }} />
      )}
    </div>
  );
};

export default GlobalActionsDropdown;
